import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
import ProfileImage from "../../Componets/ProfileImage";
const messageHandler = require("../../utils/secure/messageHandler");
const Constant = require("../Constant");

const ChatListItem = ({ chat, isSelected, onSelect }) => {
  const { currentUser } = useSecureChat();
  const [lastMessage, setLastMessage] = useState(null);
  const [isDecrypting, setIsDecrypting] = useState(false);

  // Decrypt last message
  const decryptLastMessage = useCallback(async () => {
    if (!chat.latestMessage || isDecrypting) return;

    // If latest message is deleted, don't attempt decryption
    if (chat.latestMessage?.isDeleted) {
      setLastMessage(chat.latestMessage);
      return;
    }

    try {
      setIsDecrypting(true);
      const decryptedMessage = await messageHandler.decryptMessageObject(
        chat.latestMessage,
        chat._id
      );
      setLastMessage(decryptedMessage);
      console.log("decryptedMessage on ChatListItem", decryptedMessage);
    } catch (error) {
      console.error(
        "[ChatListItem] Error decrypting last message:",
        error,
        "Message:",
        chat.latestMessage
      );
      // Show encrypted placeholder
      setLastMessage({
        ...chat.latestMessage,
        decryptedContent: "Encrypted message",
        decryptError: true,
      });
    } finally {
      setIsDecrypting(false);
    }
  }, [chat.latestMessage, chat._id]); // Remove isDecrypting from dependencies

  // Decrypt message when chat changes
  useEffect(() => {
    if (chat.latestMessage) {
      decryptLastMessage();
    } else {
      setLastMessage(null);
    }
  }, [chat.latestMessage, decryptLastMessage]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const unreadCount = Number(chat?.unreadCount) || 0;
  const hasUnread = unreadCount > 0;

  // Get display name and pic
  const displayName = chat.displayName || "Unknown User";
  const displayPic = chat.displayPic || "";

  // Proxy URL function for profile pictures
  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  return (
    <Container
      $isSelected={isSelected}
      onClick={onSelect}
      $hasUnread={hasUnread}
    >
      <AvatarContainer>
        <ProfileImage
          src={displayPic}
          name={chat.isGroupChat ? "G" : displayName}
          size={48}
          alt={displayName}
          proxiedUrl={proxiedUrl}
          avatarProps={{
            backgroundColor: chat.isGroupChat ? "var(--color-primary, #28a745)" : "#6c757d",
            color: "white",
            border: "2px solid var(--color-surface)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
          imgStyle={{
            border: "2px solid var(--color-surface)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        />
        {chat.isGroupChat && displayPic && <GroupIndicator>G</GroupIndicator>}
      </AvatarContainer>

      <ContentContainer>
        <HeaderRow>
          <Name $hasUnread={hasUnread}>{displayName}</Name>
          <Timestamp>
            {formatTimestamp(lastMessage?.createdAt || chat.updatedAt)}
          </Timestamp>
        </HeaderRow>

        <MessageRow>
          <MessagePreview $hasUnread={hasUnread}>
            {isDecrypting ? (
              <DecryptingText>Decrypting...</DecryptingText>
            ) : lastMessage ? (
              lastMessage.isDeleted ? (
                <EncryptedText>This message was deleted</EncryptedText>
              ) : lastMessage.decryptError ? (
                <EncryptedText>Encrypted message</EncryptedText>
              ) : (
                lastMessage.decryptedContent || "No messages yet"
              )
            ) : (
              "No messages yet"
            )}
          </MessagePreview>

          {hasUnread && <UnreadBadge>{unreadCount}</UnreadBadge>}
        </MessageRow>
      </ContentContainer>
    </Container>
  );
};

export default ChatListItem;

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid var(--color-border);
  background-color: ${(props) =>
    props.$isSelected ? "var(--color-background)" : "transparent"};
  border-left: ${(props) =>
    props.$isSelected
      ? "3px solid var(--color-text)"
      : "3px solid transparent"};

  &:hover {
    background-color: var(--color-background);
  }

  &:active {
    background-color: var(--color-border);
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-right: 12px;
  flex-shrink: 0;
`;

// Avatar and DefaultAvatar are now handled by ProfileImage component

const GroupIndicator = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background-color: var(--color-primary, #28a745);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  border: 2px solid var(--color-surface);
`;

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Name = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: ${(props) => (props.$hasUnread ? "700" : "500")};
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  font-family: "Merriweather", serif;
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
  margin-left: 8px;
  /* Slightly darker grey for better contrast */
  opacity: 0.8;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MessagePreview = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(props) =>
    props.$hasUnread
      ? "var(--color-text-tertiary)"
      : "var(--color-text-tertiary)"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: ${(props) => (props.$hasUnread ? "600" : "400")};
  /* Slightly darker opacity for unread messages to improve readability */
  opacity: ${(props) => (props.$hasUnread ? "1" : "0.85")};
`;

const DecryptingText = styled.span`
  color: var(--color-text-tertiary);
  font-style: italic;
  opacity: 0.8;
`;

const EncryptedText = styled.span`
  color: var(--color-text-tertiary);
  font-style: italic;
  opacity: 0.8;
`;

const UnreadBadge = styled.div`
  background-color: var(--color-text);
  color: var(--color-surface);
  border: 1px solid var(--color-surface);
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
  flex-shrink: 0;
`;
